<template>
  <div class="overview-presence ov-card">
    <div class="ov-block-title">
      <i class="material-icons ov-title-icon">location_on</i>
      <Tooltip :text="$t('dash.overview.help.presence')" position="top">
        <h2 class="ov-help-title">{{ $t('dash.overview.presence.title') }}<i class="material-icons ov-help-icon">info_outline</i></h2>
      </Tooltip>
      <span v-if="locations" class="ov-chip-muted">{{ locations.length }}</span>
      <span v-if="refreshing && locations" class="ov-updating">{{ $t('dash.overview.updating') }}</span>
      <button class="ov-arrow-link ov-block-link" @click="$emit('go', { name: 'presence-management' })">{{ $t('dash.overview.presence.open') }}<i class="material-icons">arrow_forward</i></button>
    </div>

    <div v-if="!locations" class="pm-skeleton"></div>
    <p v-else-if="!locations.length" class="ov-empty">{{ $t('dash.overview.presence.empty') }}</p>
    <div v-else class="pm-layout">
      <!-- Indicateurs -->
      <div class="pm-kpis">
        <Tooltip v-for="kpi in kpis" :key="kpi.key" :text="$t(`dash.overview.help.presence_${kpi.key}`)" position="top" class="pm-kpi-wrap">
          <div class="pm-kpi" :class="`pm-kpi--${kpi.level}`">
            <span class="pm-kpi-value">{{ kpi.value }}</span>
            <span class="pm-kpi-label">{{ $t(`dash.overview.presence.${kpi.key}`) }}</span>
          </div>
        </Tooltip>
        <div class="pm-networks">
          <span v-for="n in networks" :key="n.network" class="pm-network">{{ $t(`dash.overview.network.${n.network}`) }} <strong>{{ n.count }}</strong></span>
        </div>
      </div>

      <!-- Fiches à compléter en priorité (plus faible complétude d'abord) -->
      <div class="pm-list-wrap">
        <p class="pm-list-title">{{ $t('dash.overview.presence.to_complete') }}</p>
        <p v-if="!toComplete.length" class="ov-empty">{{ $t('dash.overview.presence.all_complete') }}</p>
        <ul v-else class="pm-list">
          <li v-for="loc in toComplete" :key="loc.id" class="pm-row" @click="openLocation(loc)">
            <div class="pm-main">
              <p class="pm-name">{{ loc.title }}</p>
              <small><span class="pm-net-tag" :class="`pm-net-tag--${loc.network}`">{{ $t(`dash.overview.network.${loc.network}`) }}</span><template v-if="loc.address">{{ loc.address }}</template></small>
            </div>
            <Tooltip :text="$t('dash.overview.presence.missing', { count: loc.missingCount })" position="top">
              <span class="pm-score">
                <span class="pm-bar"><span :class="`pm-bar--${loc.completionBand}`" :style="{ width: `${loc.completionScore}%` }"></span></span>
                {{ loc.completionScore }}%
              </span>
            </Tooltip>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Tooltip from '../tooltip/tooltipInit.vue'
import { readSection, writeSection } from './cache'

// Lecture live des fiches (API Google / Facebook / Apple) : coûteuse, on ne la relance pas plus d'une fois par heure
const REFRESH_AFTER = 60 * 60 * 1000

export default {
  name: 'OverviewPresence',

  components: { Tooltip },

  props: {
    userId: { type: String, default: null }
  },

  data() {
    const cached = readSection(this.userId, 'presence')
    return {
      cachedAt: cached ? cached.at : 0,
      cachedLocations: cached ? cached.locations : null,
      refreshing: false
    }
  },

  computed: {
    storeLocations() {
      return this.$store.state.presenceManagement.locations
    },

    // Store (déjà chargé par la section ou par ce module) sinon cache local
    locations() {
      if (this.storeLocations && this.storeLocations.length) return this.storeLocations.map(this.summarize)
      return this.cachedLocations
    },

    kpis() {
      const list = this.locations || []
      const avg = list.length ? Math.round(list.reduce((s, l) => s + (l.completionScore || 0), 0) / list.length) : 0
      const incomplete = list.filter(l => l.completionBand !== 'full').length
      const noHours = list.filter(l => !l.openDaysCount).length
      const special = list.filter(l => l.hasSpecialHoursThisWeek).length
      return [
        { key: 'completion', value: `${avg}%`, level: avg >= 80 ? 'ok' : (avg >= 50 ? 'warning' : 'danger') },
        { key: 'incomplete', value: incomplete, level: incomplete ? 'warning' : 'ok' },
        { key: 'no_hours', value: noHours, level: noHours ? 'danger' : 'ok' },
        { key: 'special_hours', value: special, level: 'info' }
      ]
    },

    networks() {
      const counts = {}
      ;(this.locations || []).forEach(l => { counts[l.network] = (counts[l.network] || 0) + 1 })
      return Object.entries(counts).map(([network, count]) => ({ network, count })).sort((a, b) => b.count - a.count)
    },

    toComplete() {
      return (this.locations || []).filter(l => l.completionBand !== 'full').sort((a, b) => a.completionScore - b.completionScore)
    }
  },

  watch: {
    storeLocations(locations) {
      if (locations && locations.length) this.persist()
    }
  },

  mounted() {
    if (this.storeLocations && this.storeLocations.length) {
      this.persist()
      return
    }
    if (!this.cachedLocations || Date.now() - this.cachedAt > REFRESH_AFTER) this.refresh()
  },

  methods: {
    // Champs utiles à l'accueil uniquement (pas de raw ni de médias dans le cache local)
    summarize(l) {
      return {
        id: l.id,
        title: l.title,
        network: l.network,
        address: l.address,
        completionScore: l.completionScore || 0,
        completionBand: l.completionBand,
        missingCount: l.missingCount || 0,
        openDaysCount: (l.openDays || []).length,
        hasSpecialHoursThisWeek: !!l.hasSpecialHoursThisWeek
      }
    },

    async refresh() {
      this.refreshing = true
      try {
        await this.$store.dispatch('presenceManagement/fetchLocations')
      } catch (e) {
        console.error('[overview] presence', e)
      } finally {
        this.refreshing = false
        // Aucune fiche : on mémorise une liste vide plutôt que de rester sur le squelette
        if (!this.storeLocations || !this.storeLocations.length) {
          this.cachedLocations = []
          this.cachedAt = Date.now()
          writeSection(this.userId, 'presence', { at: this.cachedAt, locations: [] })
        }
      }
    },

    persist() {
      const locations = this.storeLocations.map(this.summarize)
      this.cachedLocations = locations
      this.cachedAt = Date.now()
      writeSection(this.userId, 'presence', { at: this.cachedAt, locations })
    },

    openLocation(loc) {
      // L'éditeur de fiche lit la fiche dans le store : sans store chargé, on passe par la liste
      if (this.storeLocations && this.storeLocations.some(l => l.id === loc.id))
        this.$emit('go', { name: 'presence-management-location', params: { id: loc.id } })
      else
        this.$emit('go', { name: 'presence-management' })
    }
  }
}
</script>

<style scoped>
.pm-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: 20px;
}
@media (max-width: 1100px) {
  .pm-layout { grid-template-columns: 1fr; }
}
.pm-kpis {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  align-content: start;
}
.pm-kpi-wrap {
  display: block;
}
.pm-kpi {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f1edf5;
}
.pm-kpi--ok { background: #e6f9f0; }
.pm-kpi--warning { background: #fff2e6; }
.pm-kpi--danger { background: #fdf2f2; }
.pm-kpi--info { background: #f0f3ff; }
.pm-kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #121826;
}
.pm-kpi-label {
  font-size: 11px;
  font-weight: 500;
  color: #555e75;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.pm-networks {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pm-network {
  font-size: 11px;
  color: #6b1f4d;
  background: #f1edf5;
  border-radius: 20px;
  padding: 2px 9px;
}
.pm-list-title {
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #555e75;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.pm-list {
  list-style: none;
  margin: 0;
  padding: 0 4px 0 0;
  max-height: 260px;
  overflow-y: auto;
}
.pm-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 10px;
  cursor: pointer;
}
.pm-row:hover {
  background: #f6f1fa;
}
.pm-main {
  flex: 1;
  min-width: 0;
}
.pm-name {
  font-size: 13px;
  font-weight: 600;
  color: #121826;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pm-main small {
  font-size: 11px;
  color: #9ba3b7;
}
/* Tag réseau aux couleurs de la plateforme */
.pm-net-tag {
  display: inline-block;
  margin-right: 6px;
  padding: 0 7px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  color: #6b1f4d;
  background: #f1edf5;
}
.pm-net-tag--google { color: #1a73e8; background: #e8f0fe; }
.pm-net-tag--facebook { color: #1877f2; background: #e7f0fd; }
.pm-net-tag--apple { color: #1d1d1f; background: #ececee; }
.pm-score {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #121826;
  white-space: nowrap;
}
.pm-bar {
  width: 70px;
  height: 6px;
  border-radius: 3px;
  background: #f1edf5;
  overflow: hidden;
}
.pm-bar span {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: #1a9e5e;
}
.pm-bar .pm-bar--low { background: #ff6969; }
.pm-bar .pm-bar--medium { background: #ff8820; }
.pm-skeleton {
  height: 200px;
  border-radius: 5.6px;
  background: #f1edf5;
}
</style>
